/** @module Assets */
// Utils
import mimeDb from 'mime-db';

// Constants
import {
    API_BASE_PATH,
    ASSET_DOWNLOAD_URL,
    BASE_ASSETS_PATH,
    IMAGE_PATH,
    WEBINAR_ICON_ENDPOINT,
    WEBINAR_BANNER_ENDPOINT,
    WORKSPACE_APP_ICON_NAME
} from 'libs/utils/constants';
import { interpolateUrl } from 'libs/utils/url';

const TYPE_ICONS = {
    '4p_event': 'ic-star',
    act: 'ic-user-check',
    background_job: 'ic-settings',
    'content-document': 'ic-file-text',
    'content-video': 'ic-video',
    diff: 'ic-shuffle',
    editorial: 'ic-file-text',
    'fstg-page': 'ic-globe',
    'scheduled-job': 'ic-clock',
    location: 'ic-pin',
    mail_template: 'ic-at-sign',
    message: 'ic-message-circle',
    nav: 'ic-layout',
    package_install_report: 'ic-grid',
    person: 'ic-user-outline',
    presenter: 'ic-mic-outline',
    questionnaire: 'ic-help',
    session: 'ic-calendar-agenda',
    sponsor: 'ic-in-person',
    unknown: 'ic-file'
};

// some mime types have many extensions, some unusual ones, so we provide a more friendly overrides to the mimeDb representation here
const MIME_TYPE_HUMAN_REPRESENTATION = {
    'video/mp4': ['MP4'],
    'image/jpeg': ['JPEG', 'JPG']
};

export const DEFAULT_MIME_TYPE_FOR_EXTENSION = {
    mp4: 'video/mp4',
    vtt: 'text/vtt',
    txt: 'text/plain',
    ttf: 'font/ttf',
    otf: 'font/otf',
    woff: 'font/woff',
    woff2: 'font/woff2'
};

// Legacy / vendor-prefixed MIME types mapped to their canonical equivalents. Browsers and
// operating systems are inconsistent for fonts: a .woff file is frequently reported as the
// obsolete "application/font-woff" instead of "font/woff", which then fails the accept check.
export const MIME_ALIASES = {
    'application/font-woff': 'font/woff',
    'application/font-woff2': 'font/woff2',
    'application/x-font-woff': 'font/woff',
    'application/x-font-ttf': 'font/ttf',
    'application/x-font-truetype': 'font/ttf',
    'application/x-font-otf': 'font/otf',
    'application/x-font-opentype': 'font/otf'
};

/**
 * Normalizes a MIME type, mapping known legacy/vendor-prefixed aliases (see MIME_ALIASES)
 * to their canonical form. Unknown or empty values are returned unchanged.
 *
 * @param {string|null|undefined} type
 *
 * @returns {string|null|undefined}
 */
export function normalizeContentType(type) {
    if (!type) {
        return type;
    }

    return MIME_ALIASES[type] || type;
}

/**
 * The asset path. Interpolation: `{{eventId}}`, `{{filename}}`
 *
 * @constant {String} EVENT_ASSET_PATH
 *
 * @private
 */
const EVENT_ASSET_PATH = `${API_BASE_PATH}/events/{{eventId}}/assets/{{filename}}`;

/**
 * The template image path. Interpolation: `{{templateId}}`, `{{filename}}`
 *
 * @constant {String} TEMPLATE_IMAGE_PATH
 *
 * @private
 */
const TEMPLATE_IMAGE_PATH = `${API_BASE_PATH}/backstage/templates/{{templateId}}/image/{{filename}}`;

/**
 * Webinar template asset path. Interpolation: `{{templateId}}`, `{{asset}}`
 *
 * @constant {String} WEBINAR_TEMPLATE_ASSET_PATH
 *
 * @private
 */
const WEBINAR_TEMPLATE_ASSET_PATH = `${API_BASE_PATH}/webinar/template/{{templateId}}/asset/{{asset}}`;

/** *
 * @constant {String} EVENT_ICON
 *
 * @private
 */
const EVENT_ICON = `${API_BASE_PATH}/eid/{{eventId}}/event-icon`;

/**
 * @constant {string} THEME_ASSET_PATH The path for getting theme assets
 * @private
 */
const THEME_ASSET_PATH = `${API_BASE_PATH}/events/{{eventId}}/assets/theme/{{asset}}`;

/**
 * @const {string[]} SUPPORTED_AVATAR_TYPES supported avatar mime types
 */
export const SUPPORTED_AVATAR_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

/**
 * Given a relative path, this method returns the full static asset path
 *
 * @param {String} filePath the relative file path
 *
 * @return {String} the asset's path
 */
export function baseAssetsPath(filePath) {
    const path = [];

    if (window.BSTG.env !== 'test') {
        path.push(window.BSTG.env);
        path.push(window.BSTG.version);
    } else {
        filePath = filePath.substring(1, filePath.length);
    }

    return `/${path.join('/')}${filePath}`;
}

/**
 * Given a relative image path, this method returns the full static image path
 *
 * @param {String} path the relative image path
 * @param {String} [size] the image size as per `srcset` specification
 *
 * @example
 *
 * imagePath('logo.svg'); // => "/path/to/logo.svg"
 * imagePath('logo.svg', '2x'); // => "/path/to/logo.svg 2x"
 *
 * @return {String} the image's path
 */
export function imagePath(path, size = '') {
    if (size?.length) {
        size = ` ${size}`;
    }

    return baseAssetsPath(`${IMAGE_PATH}/${path}${size}`);
}

/**
 * NOTE: this method is used in the api docs api because the mapping of
 * static images is slightly different from backstage's.
 *
 * Given a relative image path, this method returns the full static image path
 *
 * @param {String} path the relative image path
 * @param {String} [size] the image size as per `srcset` specification
 *
 * @example
 *
 * imagePath('logo.svg'); // => "/path/to/logo.svg"
 * imagePath('logo.svg', '2x'); // => "/path/to/logo.svg 2x"
 *
 * @return {String} the image's path
 */
export function docsImagePath(path, size = '') {
    if (size?.length) {
        size = ` ${size}`;
    }

    const base = baseAssetsPath(`/img/${path}${size}`);
    return `${BASE_ASSETS_PATH}${base}`;
}

/**
 * Given an event ID and an optional filename this method returns a proper assets URL prefix.
 *
 * @param {String} eventId the ID of the event
 * @param {String} [filename] the name of the asset
 *
 * @returns {String} the event's asset path
 */
export function getEventAssetPath(eventId, filename = '') {
    const path = EVENT_ASSET_PATH.replace('{{eventId}}', eventId).replace('{{filename}}', filename);

    return path.endsWith('/') ? path.slice(0, -1) : path;
}

/**
 * Given a template ID and a filename this method returns a proper image path.
 *
 * @param {String} templateId the ID of the event
 * @param {String} filename the name of the image
 *
 * @returns {String} the template's image path
 */
export function getTemplateAssetPath(templateId, filename) {
    const path = TEMPLATE_IMAGE_PATH.replace('{{templateId}}', templateId).replace('{{filename}}', filename);

    return path.endsWith('/') ? path.slice(0, -1) : path;
}

/**
 * Given a webinar template ID and a asset name this method returns a proper asset path.
 *
 * @param {String} templateId the ID of the webinar template
 * @param {String} asset the name of the asset
 *
 * @returns {String} the template's asset path
 */
export function getWebinarTemplateAssetPath(templateId, asset) {
    const path = WEBINAR_TEMPLATE_ASSET_PATH.replace('{{templateId}}', templateId)
        .replace('{{asset}}', asset)
        .concat(`?_=${Date.now()}`);

    return path;
}

/**
 * Gets the standard app icon file name
 *
 * @returns {String} the app icon filename
 */
export function getAppIconFilename() {
    return WORKSPACE_APP_ICON_NAME;
}

/**
 * get the event's icon path
 *
 * @param {String} eventId event ID
 * @returns {string} path
 */
export function getEventIconImagePath(eventId) {
    return getImagePath(eventId, EVENT_ICON);
}

/**
 * get the webinar's icon path
 *
 * @param {String} eventId event ID
 * @returns {string} path
 */
export function getWebinarIconImagePath(eventId) {
    return getImagePath(eventId, WEBINAR_ICON_ENDPOINT);
}

/**
 * get the webinar's banner path
 *
 * @param {String} eventId event ID
 * @returns {string} path
 */
export function getWebinarBannerImagePath(eventId) {
    return getImagePath(eventId, WEBINAR_BANNER_ENDPOINT);
}

/**
 * get the image path
 *
 * @param {String} eventId event ID
 * @param {String} endpoint the proper endpoint to ask for the image
 * @returns {string} path
 */
export function getImagePath(eventId, endpoint) {
    let path = endpoint.replace('{{eventId}}', eventId);
    path += `?_=${Date.now()}`;
    return path;
}

/**
 * Given an fp_type this method will return the proper icon
 *
 * @param {string} type the fp type of the document
 *
 * @returns {string} the icon for the given type
 */
export function getIconForType(type) {
    return TYPE_ICONS[type] || TYPE_ICONS['unknown'];
}

/**
 * Gets the path to the requeste theme asset
 *
 * @param {string} eventId the ID of the event
 * @param {string} asset the name of the asset to load
 *
 * @returns {string} the path to the theme asset
 */
export function getThemeAsset(eventId, asset) {
    return THEME_ASSET_PATH.replace('{{eventId}}', eventId).replace('{{asset}}', asset);
}

/**
 * Returns the path for the person's picture.
 *
 * @param {string} eventId - The ID of the event.
 * @param {string} paxId - The ID of the person.
 *
 * @returns {string} The path for the person's picture.
 */
export function getPaxPicture(eventId, paxId) {
    return getDownloadUrl({
        eventId,
        attachmentName: 'photo',
        docId: `${paxId}-photo`
    });
}

/**
 * Given an array of file extensions, returns an array of associated MIME types.
 * Checks DEFAULT_MIME_TYPE_FOR_EXTENSION overrides first, then falls back to mime-db.
 *
 * @param {string[]} extensions
 * @returns {string[]}
 */
export function contentTypesByExtension(extensions) {
    if (!extensions?.length) {
        return [];
    }
    return extensions.flatMap((rawExt) => {
        const ext = rawExt?.toLowerCase();
        if (DEFAULT_MIME_TYPE_FOR_EXTENSION[ext]) {
            return [DEFAULT_MIME_TYPE_FOR_EXTENSION[ext]];
        }
        const found = Object.entries(mimeDb).find(([, data]) => data.extensions?.includes(ext));
        return found ? [found[0]] : [];
    });
}

/**
 * Given an array of content types, returns an array of associated file extensions.
 * @param {String[]} contentTypes
 * @returns {String[]}
 */
export function extensionsByContentType(contentTypes) {
    const extensions = [];
    for (const type of contentTypes) {
        const exts = mimeDb[type]?.extensions;
        if (exts) {
            extensions.push(...exts);
        }
    }

    return extensions;
}

/**
 * Given a file name, removes the extension
 * @param {String} name
 * @returns {String}
 */
export function removeExt(name) {
    return name?.replace(/\.[^.]+$/, '') || name;
}

/**
 * Converts an array of MIME types to a human-readable format.
 *
 * @param {string|string[]} mimes - A MIME type or an array of MIME types.
 *
 * @returns {string[]} An array of human-readable MIME type extensions in uppercase.
 */
export function humanReadableMime(mimes) {
    if (!Array.isArray(mimes)) {
        mimes = [mimes];
    }

    mimes = mimes.filter(Boolean);

    const allTypes = [];

    for (const mime of mimes) {
        // We skip subtype wildcards (e.g. "image/*") in the display.
        if (mime.endsWith('/*')) {
            continue;
        }

        let extensions = MIME_TYPE_HUMAN_REPRESENTATION[mime] || mimeDb[mime]?.extensions;
        if (!extensions) {
            extensions = [mime.split('/')[1] ?? mime];
        }
        const uppercased = extensions.map((e) => e.toLocaleUpperCase());
        allTypes.push(...uppercased);
    }

    return allTypes;
}

/**
 * Checks whether a concrete MIME type is permitted by a list of allowed content
 * types. Supports subtype wildcards (e.g. "image/*" matches "image/png").
 *
 * A bare "*" is intentionally NOT treated as allow-everything: only a concrete
 * type ("image/png") or a subtype wildcard ("image/*") matches.
 *
 @param {string[]} allowedTypes allowed entries, each an exact type or a "type/*" wildcard
 * @param {string} type concrete file MIME type, e.g. "image/png"
 * @returns {boolean}
 */
export function isContentTypeAllowed(allowedTypes, type) {
    type = normalizeContentType(type);

    if (!type || !allowedTypes?.length) {
        return false;
    }

    return allowedTypes.some((allowed) => {
        if (allowed === type) {
            return true;
        }

        if (allowed.endsWith('/*')) {
            return type.startsWith(allowed.slice(0, -1));
        }

        return false;
    });
}

/** @type {(assetDoc: Record<string,unknown>) => string|undefined} */
export const getContentType = (assetDoc) => {
    const files = Object.keys(assetDoc.fp_attachments_s3 || {});
    if (!files.length) {
        return undefined;
    }
    const file = files.find((d) => d === assetDoc.file_name) || files[0];
    return assetDoc.fp_attachments_s3[file]?.content_type;
};

export const FONT_MIME_TYPES = ['font/ttf', 'font/otf', 'font/woff', 'font/woff2'];

/**
 * Whether an asset document is a font.
 *
 * Prefers the stored content_type, but falls back to the file extension when it
 * is generic or missing: some OS/browser combinations don't report a MIME type
 * for woff/woff2/otf, so those uploads can end up stored as application/octet-stream.
 * The extension fallback keeps such fonts discoverable without a data migration.
 *
 * @param {Record<string,unknown>} assetDoc
 *
 * @returns {boolean}
 */
export const isFontAsset = (assetDoc) => {
    if (FONT_MIME_TYPES.includes(getContentType(assetDoc))) {
        return true;
    }

    // Fall back to the file extension. The stored content_type may be a legacy alias
    // (e.g. application/font-woff) or generic (application/octet-stream).
    // Inspect file_name as it reliably carries the real extension.
    const candidates = [assetDoc.file_name, ...Object.keys(assetDoc.fp_attachments_s3 || {})];

    return candidates.some((name) => {
        const extension = name?.split('.').pop()?.toLowerCase();
        return FONT_MIME_TYPES.includes(DEFAULT_MIME_TYPE_FOR_EXTENSION[extension]);
    });
};

/**
 * Gets the url to download file attached to a document
 *
 * @param {string} eventId id of a workspace
 * @param {string} [docId]  document id
 * @param {string} attachmentName attachment name
 * @param {string} [downloadAs] the name the file should have when downloaded
 * @returns {string|null}
 */
export const getDownloadUrl = ({ eventId, docId, attachmentName, downloadAs }) => {
    docId = docId || eventId;
    return (
        interpolateUrl(ASSET_DOWNLOAD_URL, {
            eventId,
            docId,
            fileName: attachmentName
        }) + (downloadAs ? `?download_as=${encodeURIComponent(downloadAs)}` : '')
    );
};

/**
 * returns the content type of a file
 * @param {File} file
 * @returns {string}
 */
export const getFileMime = (file) => {
    if (file.type) {
        return normalizeContentType(file.type);
    }

    // Some files are not properly detected by some OS/browser
    const extension = file.name?.split('.').pop()?.toLowerCase();
    return DEFAULT_MIME_TYPE_FOR_EXTENSION[extension] || null;
};
